import { useNavigate } from 'react-router-dom';
import { useCallback } from 'react';

/**
 * Global navigation hook for auto-navigation scenarios
 * Provides commonly used navigation patterns throughout the app
 */
export function useAutoNavigation() {
  const navigate = useNavigate();

  // Navigate to Content Generator page
  const goToContentGenerator = useCallback(() => {
    navigate('/project-manager');
  }, [navigate]);

  // Navigate to specific workbook editor (NEW ROUTE STRUCTURE)
  const goToWorkbookEditor = useCallback((workbookId: string) => {
    navigate(`/project/${workbookId}/tasks`);
  }, [navigate]);

  // Navigate to task manager for a specific goal (PROJECT INTEGRATION)
  const goToTaskManager = useCallback((goalId: string, projectId?: string) => {
    // Navigate to project route if projectId is provided
    if (projectId) {
      navigate(`/project/${projectId}/tasks`);
    } else {
      // Navigate to main task manager if no project ID
      navigate('/task-manager');
    }
  }, [navigate]);

  // Navigate to dashboard home
  const goToDashboard = useCallback(() => {
    navigate('/home');
  }, [navigate]);

  // Navigate back with fallback
  const goBack = useCallback((fallbackPath?: string) => {
    if (window.history.length > 1) {
      navigate(-1);
    } else if (fallbackPath) {
      navigate(fallbackPath);
    } else {
      goToDashboard();
    }
  }, [navigate, goToDashboard]);

  // Navigate with confirmation - TODO: Replace with useConfirmationDialog
  const navigateWithConfirmation = useCallback((
    path: string, 
    confirmationMessage: string = 'Are you sure you want to leave this page?'
  ) => {
    if (confirm(confirmationMessage)) {
      navigate(path);
    }
  }, [navigate]);

  // Auto-redirect after action (useful for delete operations)
  const redirectAfterAction = useCallback((
    action: () => Promise<void> | void,
    redirectPath: string,
    delay: number = 1000
  ) => {
    const performAction = async () => {
      try {
        await action();
        setTimeout(() => {
          navigate(redirectPath);
        }, delay);
      } catch (error) {
        console.error('Action failed, not redirecting:', error);
      }
    };
    
    return performAction();
  }, [navigate]);

  return {
    // Common destinations
    goToContentGenerator,
    goToWorkbookEditor,
    goToTaskManager,
    goToDashboard,
    goBack,
    
    // Advanced navigation
    navigateWithConfirmation,
    redirectAfterAction,
    
    // Direct access to navigate for custom cases
    navigate
  };
}
